import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Card, CardContent, CardActions, Typography, Chip, Button,
  CircularProgress, TextField, Accordion, AccordionSummary,
  AccordionDetails, Alert, Divider, LinearProgress, Tooltip, IconButton,
  Autocomplete, FormControlLabel, Checkbox,
  ToggleButton, ToggleButtonGroup, Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { getJobs, searchJobs, applyToJob, getApplications, deleteApplication } from '../services/api';
import { useNotification } from '../context/NotificationContext';
import TailorPromptModal from '../components/TailorPromptModal';
import EmptyState from '../components/EmptyState';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

const VERDICT_COLOR = {
  strong_match: 'success',
  good_match: 'warning',
  weak_match: 'default',
  no_match: 'error',
};

// Countries the search dropdown offers. "global" is a synthetic option that
// the backend expands to a curated set of major markets (see
// _GLOBAL_COUNTRIES in src/job_search.py). Order is the order shown in the
// dropdown — "global" first so it's easy to find, then alphabetical-ish by
// likely beta-user demand.
const COUNTRY_OPTIONS = [
  'global', 'br', 'us', 'gb', 'de', 'fr', 'es', 'in',
  'ca', 'au', 'nl', 'it', 'mx', 'pl', 'nz', 'sg', 'za', 'at', 'ru',
];

// Lightweight client-side mirror of the backend's _infer_locale. Used only
// for the "location is being ignored" hint — the authoritative routing
// still happens server-side. Detection is best-effort: we recognise major
// city patterns and a few country suffixes; anything unknown defaults to
// 'us', matching the backend.
const _BR_MARKERS = [
  'brasil', 'brazil',
  'sao paulo', 'são paulo',
  'rio de janeiro', 'belo horizonte', 'brasilia', 'brasília',
  'curitiba', 'porto alegre', 'florianopolis', 'florianópolis', 'recife',
  'salvador', 'fortaleza', 'manaus', 'goiania', 'goiânia', 'campinas',
];
const _CITY_TO_COUNTRY = {
  // Mostly major cities; adding more is cheap if a beta user reports an
  // edge case where the hint mis-fires.
  'london': 'gb', 'manchester': 'gb', 'edinburgh': 'gb', 'birmingham': 'gb',
  'toronto': 'ca', 'vancouver': 'ca', 'montreal': 'ca', 'montréal': 'ca',
  'berlin': 'de', 'münchen': 'de', 'munich': 'de', 'hamburg': 'de',
  'paris': 'fr', 'lyon': 'fr', 'marseille': 'fr',
  'madrid': 'es', 'barcelona': 'es', 'sevilla': 'es',
  'amsterdam': 'nl', 'rotterdam': 'nl',
  'milano': 'it', 'milan': 'it', 'roma': 'it', 'rome': 'it',
  'sydney': 'au', 'melbourne': 'au',
  'auckland': 'nz', 'wellington': 'nz',
  'mumbai': 'in', 'bangalore': 'in', 'bengaluru': 'in', 'delhi': 'in',
  'mexico city': 'mx', 'cdmx': 'mx',
  'singapore': 'sg',
  'wien': 'at', 'vienna': 'at',
  'warsaw': 'pl', 'warszawa': 'pl',
};

// IntersectionObserver-based sentinel. Triggers `onIntersect` once when
// the element first scrolls into view; the parent decides what "load more"
// means (here: bump displayLimit). Disconnects after firing so the same
// batch isn't loaded twice if the user scrolls past quickly.
function InfiniteScrollSentinel({ onIntersect }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      // Fallback: very old browser without IntersectionObserver — just
      // expand once on mount so the user still sees more results.
      onIntersect();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onIntersect();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },  // trigger 200px before reaching the sentinel
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onIntersect]);

  return (
    <Box
      ref={ref}
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ py: 3, minHeight: 60 }}
    >
      <CircularProgress size={20} />
    </Box>
  );
}

function detectLocationCountry(loc) {
  if (!loc) return null;
  const norm = loc
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (_BR_MARKERS.some(m => norm.includes(m))) return 'br';
  for (const [city, country] of Object.entries(_CITY_TO_COUNTRY)) {
    if (norm.includes(city)) return country;
  }
  // Default to US, matching backend.
  return 'us';
}

// i18n keys — resolved at render time via the t() function. Keeping a map
// of verdict → key (instead of hardcoded labels) lets the chip relocalize
// when the user toggles language without remounting the card.
const VERDICT_KEY = {
  strong_match: 'jobs.verdict_strong',
  good_match:   'jobs.verdict_good',
  weak_match:   'jobs.verdict_weak',
  no_match:     'jobs.verdict_no',
};

// Escape a string so it can be embedded literally inside a regex. Skills
// often contain regex specials (".NET", "C++", "Node.js") so this matters.
function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Highlight every occurrence of matching/missing skills inside the job
// description. Matching skills get a green highlight; missing skills get
// an amber highlight. Case-insensitive. Supports multi-word skills and
// names with non-word characters (C++, C#, React.js) by using lookbehind/
// lookahead (?<!\w)/(?!\w) instead of \b — \b fails at "++" and "#" edges.
//
// Skills are sorted longest-first in the alternation so "React.js" wins
// over "React" when both are present.
function HighlightedDescription({ text, matching = [], missing = [] }) {
  const content = useMemo(() => {
    if (!text) return null;

    const matchingSet = new Set(matching.filter(Boolean).map(s => s.toLowerCase()));
    const missingSet = new Set(missing.filter(Boolean).map(s => s.toLowerCase()));
    const allSkills = [...matchingSet, ...missingSet]
      .filter(s => s.length >= 2)                           // avoid 1-char noise
      .sort((a, b) => b.length - a.length);

    if (allSkills.length === 0) return [{ type: 'plain', content: text }];

    let pattern;
    try {
      pattern = new RegExp(
        `(?<!\\w)(${allSkills.map(escapeRegExp).join('|')})(?!\\w)`,
        'gi',
      );
    } catch {
      // Very old browsers without lookbehind — fall back to \b. Correct for
      // alphanumeric skills, imperfect for "C++" etc., but never crashes.
      pattern = new RegExp(
        `\\b(${allSkills.map(escapeRegExp).join('|')})\\b`,
        'gi',
      );
    }

    const segments = [];
    let last = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      if (m.index > last) segments.push({ type: 'plain', content: text.slice(last, m.index) });
      const hit = m[0];
      const lower = hit.toLowerCase();
      const type = matchingSet.has(lower) ? 'matching' : 'missing';
      segments.push({ type, content: hit });
      last = m.index + hit.length;
    }
    if (last < text.length) segments.push({ type: 'plain', content: text.slice(last) });
    return segments;
  }, [text, matching, missing]);

  if (!content) return null;

  return (
    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
      {content.map((seg, i) => {
        if (seg.type === 'matching') {
          return (
            <Box
              component="mark"
              key={i}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.22),
                color: 'success.dark',
                fontWeight: 600,
                px: 0.4,
                borderRadius: 0.5,
              }}
            >
              {seg.content}
            </Box>
          );
        }
        if (seg.type === 'missing') {
          return (
            <Box
              component="mark"
              key={i}
              sx={{
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.22),
                color: 'warning.dark',
                fontWeight: 600,
                px: 0.4,
                borderRadius: 0.5,
              }}
            >
              {seg.content}
            </Box>
          );
        }
        return <span key={i}>{seg.content}</span>;
      })}
    </Typography>
  );
}

// Ghost-job badge config. `likely_real` gets no badge at all — showing a
// green "Real job ✓" on every card would be noise. Only surface when there's
// something to flag.
// Only color + icon stay in code; label comes from i18n at render time.
const GHOST_BADGE_META = {
  uncertain:    { color: 'warning', Icon: WarningAmberIcon, labelKey: 'jobs.ghost_uncertain' },
  likely_ghost: { color: 'error',   Icon: BlockIcon,        labelKey: 'jobs.ghost_likely' },
};

function GhostBadge({ verdict, probability, signals }) {
  const { t } = useTranslation();
  const cfg = GHOST_BADGE_META[verdict];
  if (!cfg) return null;
  const { Icon, color, labelKey } = cfg;
  const label = t(labelKey);
  const pct = Math.round((probability || 0) * 100);
  const tooltip = (
    <Box sx={{ maxWidth: 260 }}>
      <Typography variant="caption" display="block" fontWeight={600} mb={0.5}>
        {t('jobs.ghost_probability', { pct })}
      </Typography>
      {signals && signals.length > 0 ? (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {signals.map((s, i) => (
            <li key={i}><Typography variant="caption">{s}</Typography></li>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="inherit">{t('jobs.ghost_no_signals')}</Typography>
      )}
    </Box>
  );
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Chip
        icon={<Icon />}
        label={label}
        size="small"
        color={color}
        variant="outlined"
      />
    </Tooltip>
  );
}

function JobCard({ job, savedIds, savedAppIdByJobId, tailoredIds, onSaved, onUnsaved, onNotify, onApplyClick }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  // Match reason is clamped to 3 lines by default; click toggles full text
  // inline so user can read the full Claude analysis before deciding to apply.
  const [reasonExpanded, setReasonExpanded] = useState(false);
  const applied = savedIds.has(job.id);
  const isTailored = tailoredIds.has(job.id);
  const score = Math.round(job.match_score || 0);
  const matchingSkills = typeof job.matching_skills === 'string'
    ? JSON.parse(job.matching_skills || '[]')
    : job.matching_skills || [];
  const missingSkills = typeof job.missing_skills === 'string'
    ? JSON.parse(job.missing_skills || '[]')
    : job.missing_skills || [];

  // Coverage = matching / (matching + missing). Guarded so cards without any
  // skill signal still render (avoid NaN / 0-div).
  const totalSkills = matchingSkills.length + missingSkills.length;
  const coveragePct = totalSkills > 0
    ? Math.round((matchingSkills.length / totalSkills) * 100)
    : null;

  const handleAddToTracker = async () => {
    setLoading(true);
    try {
      const res = await applyToJob(job.id);
      // Backend returns the Application row — remember its id so a later
      // click on the filled bookmark can remove it.
      onSaved(job.id, res.data?.id);
      onNotify('success', t('jobs.added_toast', { title: job.title }));
    } catch (e) {
      onNotify('error', e.friendlyMessage || t('jobs.save_failed'));
    }
    setLoading(false);
  };

  // Click on a filled bookmark removes the tracked application. No
  // confirmation dialog here — the action is cheap to undo (just click
  // bookmark again) and an extra tap would feel heavy on a hover-like
  // toggle. The Dashboard page still uses a confirmation since that's
  // the "manage" surface.
  const handleUnsave = async () => {
    const appId = savedAppIdByJobId?.get?.(job.id);
    if (!appId) {
      onNotify('error', t('jobs.remove_lookup_failed'));
      return;
    }
    setLoading(true);
    try {
      await deleteApplication(appId);
      onUnsaved(job.id);
      onNotify('success', t('jobs.removed_toast', { title: job.title }));
    } catch (e) {
      onNotify('error', e.friendlyMessage || t('jobs.remove_failed'));
    }
    setLoading(false);
  };

  const handleBookmarkClick = () => applied ? handleUnsave() : handleAddToTracker();

  // Apply button → save to tracker (if needed) then open tailor modal.
  // We always save before opening the modal because tailoring requires an
  // Application row with the description attached.
  const handleApplyClick = async () => {
    setApplying(true);
    try {
      const res = await applyToJob(job.id);
      onSaved(job.id);
      onApplyClick(res.data);
    } catch (e) {
      onNotify('error', e.friendlyMessage || t('jobs.save_failed'));
    }
    setApplying(false);
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { borderColor: 'text.secondary' }, transition: 'border-color 0.15s' }}>
      <CardContent sx={{ flex: 1 }}>
        {/* Header — title/company on the left, score + bookmark icon on the right.
            minWidth:0 on the flex child is required so long titles don't force
            the container past the card edge and hide the score+icon. */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box flex={1} pr={1} sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              gutterBottom
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {job.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{job.company}</Typography>
            <Typography variant="caption" color="text.disabled" noWrap display="block">{job.location}</Typography>
          </Box>
          <Box display="flex" alignItems="flex-start" gap={0.5}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ lineHeight: 1.2 }}
              color={score >= 80 ? 'success.main' : score >= 60 ? 'warning.main' : 'text.secondary'}
            >
              {score}
            </Typography>
            <Tooltip title={applied ? t('jobs.click_to_remove') : t('jobs.save_to_tracker')} arrow>
              {/* span wrapper lets Tooltip target a disabled button (loading state) */}
              <span>
                <IconButton
                  size="small"
                  onClick={handleBookmarkClick}
                  disabled={loading}
                  sx={{
                    color: applied ? 'primary.main' : 'text.secondary',
                    '&:hover': { color: applied ? 'error.main' : 'primary.main' },
                    mt: -0.25,
                  }}
                  aria-label={applied ? t('jobs.remove_aria') : t('jobs.save_aria')}
                >
                  {loading
                    ? <CircularProgress size={16} />
                    : applied
                      ? <BookmarkIcon fontSize="small" />
                      : <BookmarkBorderIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Match + coverage consolidated: one bar, one label line.
            Match is the primary signal; coverage is shown textually
            alongside so the card stays compact. */}
        <Box mb={1.5}>
          <LinearProgress
            variant="determinate"
            value={score}
            color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'inherit'}
            sx={{ borderRadius: 2, mb: 0.5, height: 6 }}
          />
          <Typography variant="caption" color="text.secondary">
            <Box component="span" fontWeight={600} color="text.primary">{t('jobs.match_label', { score })}</Box>
            {coveragePct !== null && (
              <>
                {' · '}
                <Box component="span" fontWeight={600} color="text.primary">
                  {t('jobs.skills_count', { matched: matchingSkills.length, total: totalSkills })}
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {/* Verdict */}
        <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
          <Chip
            label={VERDICT_KEY[job.match_verdict] ? t(VERDICT_KEY[job.match_verdict]) : (job.match_verdict || '')}
            color={VERDICT_COLOR[job.match_verdict] || 'default'}
            size="small"
            icon={<TrendingUpIcon />}
          />
          <GhostBadge
            verdict={job.ghost_verdict}
            probability={job.ghost_probability}
            signals={
              typeof job.ghost_signals === 'string'
                ? (() => { try { return JSON.parse(job.ghost_signals || '[]'); } catch { return []; } })()
                : (job.ghost_signals || [])
            }
          />
          {isTailored && (
            <Chip label={t('jobs.tailored_chip')} size="small" color="primary" variant="outlined" />
          )}
        </Box>

        {/* Reason — clamped to 3 lines by default so cards in the grid stay
            visually consistent. Click toggles the full text inline so the
            user can read the full Claude analysis before deciding to apply.
            Full reason is also preserved in the JobListing record on the
            tracked application's detail surface. */}
        <Typography
          variant="body2"
          color="text.secondary"
          mb={1.5}
          onClick={() => setReasonExpanded(prev => !prev)}
          title={reasonExpanded ? t('jobs.reason_collapse') : t('jobs.reason_expand')}
          sx={{
            ...(reasonExpanded ? {} : {
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }),
            lineHeight: 1.45,
            cursor: 'pointer',
            transition: 'color 0.15s',
            '&:hover': { color: 'text.primary' },
          }}
        >
          {job.match_reason}
        </Typography>

        {/* Skills */}
        {matchingSkills.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{t('jobs.matching')}</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {matchingSkills.slice(0, 5).map(skill => (
                <Chip key={skill} label={skill} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {missingSkills.length > 0 && (
          <Box mb={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{t('jobs.missing')}</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {missingSkills.slice(0, 3).map(skill => (
                <Chip key={skill} label={skill} size="small" variant="outlined" sx={{ color: 'text.secondary', borderColor: 'divider' }} />
              ))}
            </Box>
          </Box>
        )}

        {/* Description — with matching skills highlighted in green and missing in amber */}
        {job.description && (
          <Accordion elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="caption" fontWeight="bold">{t('jobs.view_description')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {(matchingSkills.length > 0 || missingSkills.length > 0) && (
                <Box
                  display="flex"
                  gap={1.5}
                  mb={1.5}
                  sx={{
                    fontSize: 11,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {matchingSkills.length > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: 0.5,
                        bgcolor: (theme) => alpha(theme.palette.success.main, 0.35),
                      }} />
                      <span>{t('jobs.matching')}</span>
                    </Box>
                  )}
                  {missingSkills.length > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: 0.5,
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.35),
                      }} />
                      <span>{t('jobs.missing')}</span>
                    </Box>
                  )}
                </Box>
              )}
              <HighlightedDescription
                text={job.description}
                matching={matchingSkills}
                missing={missingSkills}
              />
              {/* Secondary metadata — source + posted date, low-importance
                  info moved out of the main card face to reduce chip noise. */}
              {(job.source || job.date_posted) && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                  mt={2}
                >
                  {job.source && t(job.source === 'adzuna' ? 'jobs.source_adzuna' : 'jobs.source_google')}
                  {job.source && job.date_posted && ' · '}
                  {job.date_posted && t('jobs.posted_date', { date: job.date_posted })}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>

      {/* Single primary CTA in the footer. Save moved to the bookmark icon
          in the header — this keeps the card focused on one action. */}
      <CardActions sx={{ p: 2, pt: 0, mt: 'auto' }}>
        <Button
          variant="contained"
          size="medium"
          fullWidth
          onClick={handleApplyClick}
          disabled={applying || !job.url}
          endIcon={applying ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon />}
        >
          {applying ? t('jobs.opening') : t('jobs.apply')}
        </Button>
      </CardActions>
    </Card>
  );
}

export default function Jobs() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [location, setLocation] = useState('New York');
  // 7 days default covers BR + international markets without too many
  // ghost postings. User can tighten via Advanced Filters.
  const [maxDays, setMaxDays] = useState(7);
  const [minScore, setMinScore] = useState(60);
  // countries: empty array = auto-detect from location (single-country
  // search, the legacy behaviour). User picks one or more codes (or
  // "global") to fan out across multiple Adzuna endpoints.
  const [countries, setCountries] = useState([]);
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [message, setMessage] = useState('');
  const [savedIds, setSavedIds] = useState(new Set());
  // Map<jobId, applicationId> — lets the JobCard's bookmark-toggle call
  // DELETE /applications/{id} when the user un-saves.
  const [savedAppIdByJobId, setSavedAppIdByJobId] = useState(new Map());
  const [tailoredIds, setTailoredIds] = useState(new Set());
  const [tailorTarget, setTailorTarget] = useState(null); // Application object for modal
  // Infinite-scroll pagination — render PAGE_SIZE cards initially, expand
  // as the user scrolls past the sentinel. The full result set is already
  // in state (no extra API calls); this just delays DOM creation so the
  // first paint stays fast even with 50 cards.
  const PAGE_SIZE = 20;
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);
  const { success, error: notifyError, info } = useNotification();

  const handleSaved = (jobId, appId) => {
    setSavedIds(prev => new Set([...prev, jobId]));
    if (appId != null) {
      setSavedAppIdByJobId(prev => new Map(prev).set(jobId, appId));
    }
  };

  const handleUnsaved = (jobId) => {
    setSavedIds(prev => {
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
    setSavedAppIdByJobId(prev => {
      const next = new Map(prev);
      next.delete(jobId);
      return next;
    });
    setTailoredIds(prev => {
      const next = new Set(prev);
      next.delete(jobId);
      return next;
    });
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const [jobsRes, appsRes] = await Promise.all([getJobs(), getApplications()]);
      const jobs = jobsRes.data;
      const apps = appsRes.data;

      const savedSet = new Set();
      const tailoredSet = new Set();
      const appIdMap = new Map();
      jobs.forEach(job => {
        const match = apps.find(
          app => app.company === job.company && app.role === job.title
        );
        if (match) {
          savedSet.add(job.id);
          appIdMap.set(job.id, match.id);
          if (match.has_tailored) tailoredSet.add(job.id);
        }
      });

      setJobs(jobs);
      setSavedIds(savedSet);
      setSavedAppIdByJobId(appIdMap);
      setTailoredIds(tailoredSet);
    } catch (e) {
      notifyError(e.friendlyMessage || t('jobs.load_failed'));
    }
    setLoading(false);
  };

  useEffect(() => { loadJobs(); }, []);

  // Hide jobs without descriptions — they can't be tailored and pollute the list
  const visibleJobs = jobs.filter(j => j.description && j.description.trim());

  const handleSearch = async () => {
    setSearching(true);
    setMessage('');
    // Reset pagination so the new result set starts from the top.
    setDisplayLimit(PAGE_SIZE);
    try {
      const res = await searchJobs({
        location,
        max_days: maxDays,
        min_score: minScore,
        // Only send non-empty arrays — preserves backend default behaviour
        // (auto-detect single country from location) when user hasn't
        // touched the country picker.
        countries: countries.length > 0 ? countries : undefined,
        remote_only: remoteOnly,
      });
      setJobs(res.data.jobs || []);
      const msg = t('jobs.search_keywords_msg', {
        count: res.data.found,
        keywords: res.data.keywords?.join(', ') || '',
      });
      setMessage(msg);
      info(msg);
    } catch (e) {
      const msg = e.friendlyMessage || t('jobs.search_failed');
      setMessage(msg);
      notifyError(msg);
    }
    setSearching(false);
  };

  return (
    <Box p={3}>
      {/* Page title moved to the top app bar — see Navbar.TITLE_BY_PATH */}

      {/* Search controls — progressive disclosure pattern.
          Always visible: Location, Countries, Remote toggle, Search button.
          Behind "Advanced filters" accordion: Job age + Min match (chip
          groups, not sliders — touch-friendlier and value-discrete).
          Defaults (3 days, 60% match) are sane enough that ~90% of users
          never need to expand the advanced section. */}
      <Box sx={{ mb: 3 }}>
        {/* ── Always-visible row ────────────────────────────────────────── */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} order={{ xs: 1, sm: 1 }}>
            <TextField
              label={t('jobs.location_label')}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              fullWidth
              size="small"
            />
            {/* Hint: location is silently dropped by the backend when none
                of the explicit countries match the city's country (e.g.
                "New York" + [BR, GB, CA] → location ignored). Surface this
                so the user understands the broad-country search behaviour
                instead of wondering why off-city results appear. */}
            {(() => {
              const detected = detectLocationCountry(location.trim());
              const hasGlobal = countries.includes('global');
              const conflict =
                location.trim() &&
                countries.length > 0 &&
                !hasGlobal &&
                detected &&
                !countries.includes(detected);
              if (!conflict) return null;
              const labels = countries.map(c => t(`countries.${c}`, c.toUpperCase())).join(', ');
              return (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}
                >
                  {t('jobs.location_ignored_hint', { countries: labels })}
                </Typography>
              );
            })()}
          </Grid>

          <Grid item xs={12} sm={4} order={{ xs: 2, sm: 2 }}>
            <Autocomplete
              multiple
              size="small"
              options={COUNTRY_OPTIONS}
              value={countries}
              onChange={(_, newValue) => {
                // If the user picks "global", clear the rest — "global" is
                // a complete preset, mixing it with explicit codes is
                // confusing (the backend would also dedupe).
                if (newValue.includes('global') && !countries.includes('global')) {
                  setCountries(['global']);
                } else if (countries.includes('global') && newValue.length > 1) {
                  // Adding another country while "global" is selected →
                  // drop "global" and keep the new explicit codes.
                  setCountries(newValue.filter(c => c !== 'global'));
                } else {
                  setCountries(newValue);
                }
              }}
              getOptionLabel={(code) => t(`countries.${code}`, code.toUpperCase())}
              renderTags={(value, getTagProps) =>
                value.map((code, index) => (
                  <Chip
                    label={t(`countries.${code}`, code.toUpperCase())}
                    size="small"
                    {...getTagProps({ index })}
                    key={code}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('jobs.countries_label')}
                  placeholder={countries.length === 0 ? t('jobs.countries_placeholder') : ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} sm={2} order={{ xs: 3, sm: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={remoteOnly}
                  onChange={(e) => setRemoteOnly(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">{t('jobs.remote_only_label')}</Typography>
              }
            />
          </Grid>

          <Grid item xs={6} sm={3} order={{ xs: 4, sm: 4 }}>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={handleSearch}
              disabled={searching}
              sx={{ textTransform: 'none' }}
            >
              {searching ? t('common.searching') : t('jobs.search_button')}
            </Button>
          </Grid>
        </Grid>

        {/* ── Collapsible advanced filters ────────────────────────────────
            Sane defaults already cover the common case; expand to fine-tune. */}
        <Accordion
          elevation={0}
          disableGutters
          sx={{
            mt: 1,
            bgcolor: 'transparent',
            '&::before': { display: 'none' },           // remove MUI's default top border line
            '& .MuiAccordionSummary-root': { minHeight: 36, px: 0 },
            '& .MuiAccordionSummary-content': { my: 0 },
            '& .MuiAccordionDetails-root': { px: 0, pt: 1 },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('jobs.advanced_filters')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              {/* Job age — discrete chip group (1/3/7/14 days) */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {t('jobs.max_age_label')}
                </Typography>
                <ToggleButtonGroup
                  value={maxDays}
                  exclusive
                  onChange={(_, v) => v !== null && setMaxDays(v)}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
                >
                  {[1, 3, 7, 14].map(d => (
                    <ToggleButton key={d} value={d}>
                      {t('jobs.max_age_chip', { count: d })}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* Min match score — discrete chip group (40/50/60/70/80%) */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {t('jobs.min_score_label_short')}
                </Typography>
                <ToggleButtonGroup
                  value={minScore}
                  exclusive
                  onChange={(_, v) => v !== null && setMinScore(v)}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
                >
                  {[40, 50, 60, 70, 80].map(s => (
                    <ToggleButton key={s} value={s}>{s}%</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {message && <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>{message}</Alert>}
      </Box>

      {/* Jobs Grid */}
      {loading || searching ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : visibleJobs.length === 0 ? (
        <EmptyState
          icon={<TravelExploreIcon />}
          title={t('jobs.empty_title')}
          body={t('jobs.empty_body')}
          sx={{ mt: 4 }}
        />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('jobs.n_jobs_found', { count: visibleJobs.length })}
          </Typography>
          <Grid container spacing={2}>
            {visibleJobs.slice(0, displayLimit).map(job => (
              <Grid item xs={12} sm={6} md={4} key={job.id}>
                <JobCard
                  job={job}
                  savedIds={savedIds}
                  savedAppIdByJobId={savedAppIdByJobId}
                  tailoredIds={tailoredIds}
                  onSaved={handleSaved}
                  onUnsaved={handleUnsaved}
                  onNotify={(sev, msg) => sev === 'success' ? success(msg) : notifyError(msg)}
                  onApplyClick={(appData) => setTailorTarget(appData)}
                />
              </Grid>
            ))}
          </Grid>
          {/* Sentinel for infinite scroll. When this Box scrolls into the
              viewport, IntersectionObserver bumps displayLimit by PAGE_SIZE,
              rendering the next batch of cards. No extra API calls — all
              data is already in memory; we just delay DOM creation. */}
          {displayLimit < visibleJobs.length && (
            <InfiniteScrollSentinel
              onIntersect={() => setDisplayLimit(prev => prev + PAGE_SIZE)}
            />
          )}
        </>
      )}

      <TailorPromptModal
        open={!!tailorTarget}
        onClose={() => setTailorTarget(null)}
        application={tailorTarget}
      />
    </Box>
  );
}
